import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import type { Admin, OutreachProspect } from "@lumika/shared";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../../api/admin";
import {
  ProspectFormFields,
  ProspectFormSchema,
  toProspectEditValues,
  toProspectPayload,
} from "../ProspectForm";
import { ActivityLog } from "../ActivityLog";

type Props = {
  prospect: OutreachProspect | null;
  admins: Admin[];
  onClose: () => void;
};

/**
 * Right-side slide-over panel for a kanban card. Hosts quick-edit fields
 * (via ProspectFormFields) and the activity log — all reused from existing
 * components, no duplication.
 */
export function ProspectPanel({ prospect, admins, onClose }: Props) {
  return (
    <Sheet open={Boolean(prospect)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[90vw] max-w-xl">
        {prospect && (
          <PanelBody key={prospect.id} prospect={prospect} admins={admins} onClose={onClose} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function PanelBody({
  prospect,
  admins,
  onClose,
}: {
  prospect: OutreachProspect;
  admins: Admin[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const baseline = toProspectEditValues(prospect);
  const { values, errors, setField, validate } = useZodForm(
    ProspectFormSchema,
    baseline,
  );
  const isDirty = JSON.stringify(values) !== JSON.stringify(baseline);

  const updateMutation = useMutation({
    mutationFn: () => adminApi.updateOutreachProspect(prospect.id, toProspectPayload(values)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["outreach"] });
      toast.success("Saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function save() {
    if (!validate().ok) return;
    updateMutation.mutate();
  }

  return (
    <>
      <SheetHeader className="pr-10">
        <SheetTitle className="truncate">{prospect.companyName}</SheetTitle>
      </SheetHeader>

      <SheetBody className="flex flex-col gap-6">
        {/* Quick-edit form */}
        <div className="grid gap-5">
          <ProspectFormFields values={values} errors={errors} setField={setField} admins={admins} />
        </div>

        {/* Activity log */}
        <ActivityLog prospectId={prospect.id} admins={admins} />
      </SheetBody>

      <SheetFooter>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/outreach/${prospect.id}`} onClick={onClose}>
            <ExternalLink className="h-4 w-4" />
            Full detail
          </Link>
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={!isDirty || updateMutation.isPending}
        >
          {updateMutation.isPending ? "Saving…" : "Save"}
        </Button>
      </SheetFooter>
    </>
  );
}
